/* Moments Etched v15.4.7.27 — cart interaction guardrails + anchored project removal. */
(() => {
  'use strict';

  const VERSION = '15.4.7.27';
  const root = () => window.Shopify?.routes?.root || '/';

  function setSessionNotice(message, tone = 'success') {
    try {
      sessionStorage.setItem('me:cart-interaction-notice', JSON.stringify({ message, tone, at: Date.now() }));
    } catch (_) {}
  }

  function consumeSessionNotice() {
    try {
      const raw = sessionStorage.getItem('me:cart-interaction-notice');
      if (!raw) return null;
      sessionStorage.removeItem('me:cart-interaction-notice');
      const parsed = JSON.parse(raw);
      if (!parsed?.message || Date.now() - Number(parsed.at || 0) > 120000) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function showCartNotice(message, tone = 'success') {
    const host = document.querySelector('.cart-items__wrapper, [data-cart-items], main');
    if (!host || !message) return;
    let notice = document.querySelector('[data-me-cart-interaction-notice]');
    if (!notice) {
      notice = document.createElement('div');
      notice.dataset.meCartInteractionNotice = '';
      notice.className = 'me-cart-interaction-notice';
      notice.setAttribute('role', tone === 'error' ? 'alert' : 'status');
      notice.setAttribute('aria-live', tone === 'error' ? 'assertive' : 'polite');
      host.prepend(notice);
    }
    notice.classList.toggle('is-error', tone === 'error');
    notice.textContent = message;
    window.setTimeout(() => notice?.classList.add('is-visible'), 20);
  }

  function openRemoveConfirmation(row) {
    const panel = row?.querySelector('[data-me-project-remove-confirmation]');
    const trigger = row?.querySelector('[data-me-group-remove]');
    if (!panel || !trigger) return;
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    row.classList.add('me-project-remove-is-open');
    const confirm = panel.querySelector('[data-me-confirm-remove-project]');
    window.requestAnimationFrame(() => confirm?.focus({ preventScroll: true }));
  }

  function closeRemoveConfirmation(row, returnFocus = true) {
    const panel = row?.querySelector('[data-me-project-remove-confirmation]');
    const trigger = row?.querySelector('[data-me-group-remove]');
    if (!panel || !trigger) return;
    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    row.classList.remove('me-project-remove-is-open');
    const status = panel.querySelector('[data-me-remove-project-status]');
    if (status) status.textContent = '';
    if (returnFocus) trigger.focus({ preventScroll: true });
  }

  async function fetchCart() {
    const response = await fetch(`${root()}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('The cart could not be read. Please refresh and try again.');
    return response.json();
  }

  async function removeProject(row) {
    if (!row || row.dataset.meRemoveBusy === 'true') return;
    const groupId = String(row.dataset.meGroupId || '').trim();
    const panel = row.querySelector('[data-me-project-remove-confirmation]');
    const status = panel?.querySelector('[data-me-remove-project-status]');
    const controls = row.querySelectorAll('button, input, select, a');
    row.dataset.meRemoveBusy = 'true';
    row.setAttribute('aria-busy', 'true');
    controls.forEach((control) => {
      if ('disabled' in control) control.disabled = true;
      control.setAttribute?.('aria-disabled', 'true');
    });
    if (status) status.textContent = 'Removing the complete project…';

    try {
      const cart = await fetchCart();
      const projectLines = (cart.items || []).filter((line) =>
        String(line.properties?.['_ME Design Group'] || '').trim() === groupId
      );
      if (!projectLines.length) {
        throw new Error('This personalized project is no longer in the cart. Refresh the page to continue.');
      }

      const updates = Object.fromEntries(projectLines.map((line) => [line.key, 0]));
      const response = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.errors) {
        throw new Error(payload.description || payload.message || 'Shopify could not remove the complete project.');
      }

      setSessionNotice('Personalized project removed from your cart.');
      window.location.reload();
    } catch (error) {
      console.error(`[Moments v${VERSION}] Project removal failed.`, error);
      row.dataset.meRemoveBusy = 'false';
      row.removeAttribute('aria-busy');
      controls.forEach((control) => {
        if ('disabled' in control) control.disabled = false;
        control.removeAttribute?.('aria-disabled');
      });
      if (status) status.textContent = error.message || 'The project could not be removed. Your cart was not changed.';
      showCartNotice(error.message || 'The project could not be removed. Your cart was not changed.', 'error');
    }
  }

  function syncDetailsLabel(details) {
    const label = details?.querySelector('[data-me-project-details-label]');
    if (!label) return;
    label.textContent = details.open
      ? 'Hide project details & production mockups'
      : 'View project details & production mockups';
  }

  document.addEventListener('DOMContentLoaded', () => {
    const notice = consumeSessionNotice();
    if (notice) showCartNotice(notice.message, notice.tone);
    document.querySelectorAll('.me-grouped-cart__details').forEach(syncDetailsLabel);
  });

  // The atomic size editor emits this immediately before its authoritative cart reload.
  document.addEventListener('moments:cart-updated', () => {
    setSessionNotice('Sizes and quantities updated. Your artwork and personalization were kept with the project.');
  });

  // Capture the trash action before legacy grouped-cart listeners can delete a full
  // personalization project on the first click.
  document.addEventListener('click', (event) => {
    const removeTrigger = event.target.closest?.('[data-me-group-remove]');
    if (removeTrigger) {
      const row = removeTrigger.closest('[data-me-personalized-group]');
      if (!row) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      openRemoveConfirmation(row);
      return;
    }

    const cancel = event.target.closest?.('[data-me-cancel-remove-project]');
    if (cancel) {
      const row = cancel.closest('[data-me-personalized-group]');
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      closeRemoveConfirmation(row, true);
      return;
    }

    const confirm = event.target.closest?.('[data-me-confirm-remove-project]');
    if (confirm) {
      const row = confirm.closest('[data-me-personalized-group]');
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      removeProject(row);
      return;
    }

    // Treat the confirmation as a true anchored popover: clicking elsewhere
    // dismisses it and returns the cart to its normal compact state.
    const openPanel = document.querySelector('[data-me-project-remove-confirmation]:not([hidden])');
    if (openPanel && !openPanel.contains(event.target)) {
      closeRemoveConfirmation(openPanel.closest('[data-me-personalized-group]'), false);
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openPanel = document.querySelector('[data-me-project-remove-confirmation]:not([hidden])');
    if (!openPanel) return;
    event.preventDefault();
    closeRemoveConfirmation(openPanel.closest('[data-me-personalized-group]'), true);
  });

  document.addEventListener('toggle', (event) => {
    const details = event.target.closest?.('.me-grouped-cart__details');
    if (details) syncDetailsLabel(details);
  }, true);
})();
